/**
 * Active Invites Status — React parity with Angular `ReportsInvitesComponent` (report mode).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Download, Loader2 } from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { ListSearchToolbar } from '@/components/ui/ListSearchToolbar'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { LIBERTY_ROLES } from '@/constants/roles'
import { useUserRole } from '@/hooks/useUserRole'
import { useIsMobile } from '@/hooks/useIsMobile'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { useAppDispatch } from '@/app/hooks'
import { logout } from '@/features/auth/authSlice'
import { logout as logoutApi } from '@/lib/api/authService'
import { ReportTableScroll } from '../components/ReportTableScroll'
import { REPORT_EXPORT_BUTTON_CLASS } from '../constants/configuredReportStyles'
import { InviteeFilters } from '@/features/invitee/components/InviteeFilters'
import { InviteeTable } from '@/features/invitee/components/InviteeTable'
import { InviteeGrid } from '@/features/invitee/components/InviteeGrid'
import { EditInviteeDialog } from '@/features/invitee/components/EditInviteeDialog'
import { InviteActionsDialog } from '@/features/invitee/components/InviteActionsDialog'
import { useInviteeList } from '@/features/invitee/hooks/useInviteeList'
import { PER_PAGE_OPTIONS } from '@/features/invitee/utils/inviteeConstants'
import {
  getInviteeList,
  resendInvitation,
} from '@/features/invitee/services/inviteeService'
import { exportInviteeList } from '@/features/reports/services/reportsService'
import type { Invitee } from '@/features/invitee/types/invitee'

export function ActiveInvitesStatusPage() {
  const dispatch = useAppDispatch()
  const isMobile = useIsMobile()
  const [isListView, setIsListView] = useState(true)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [searchDraft, setSearchDraft] = useState('')
  const [exporting, setExporting] = useState(false)

  const {
    hasPolicy,
    hasWriteAccess,
    isLibertyRole,
    orgId,
    companyId,
    propertyId,
    roleName,
  } = useUserRole()

  const canEditInvite = hasPolicy(['invite_edit'])
  const canApplyNow = hasWriteAccess('invite')
  const canExport =
    isLibertyRole || (LIBERTY_ROLES as readonly string[]).includes(roleName)

  const lockedOrgId = orgId != null ? String(orgId) : undefined
  const lockedCompanyId = companyId != null ? String(companyId) : undefined
  const lockedPropertyId = propertyId != null ? String(propertyId) : undefined

  const {
    invitees,
    loading,
    pagination,
    filters,
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
    refetch,
  } = useInviteeList()

  useEffect(() => {
    setIsListView(!isMobile)
  }, [isMobile])

  useEffect(() => {
    setSearchDraft(filters.q || '')
  }, [filters.q])

  useEffect(() => {
    const timer = setTimeout(() => {
      const nextQ = searchDraft.trim()
      if (nextQ !== (filters.q || '').trim()) {
        applyFilters({ q: nextQ })
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [searchDraft, filters.q, applyFilters])

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (filters.q?.trim()) count++
    if (filters.org_id && filters.org_id !== lockedOrgId) count++
    if (filters.company_id && filters.company_id !== lockedCompanyId) count++
    if (filters.property_id && filters.property_id !== lockedPropertyId) count++
    if (filters.inviteStatus?.length) count++
    if (filters.fromDt || filters.toDt) count++
    return count
  }, [filters, lockedOrgId, lockedCompanyId, lockedPropertyId])

  const handleExport = useCallback(async () => {
    if (!invitees.length) return
    setExporting(true)
    try {
      const url = await exportInviteeList(filters)
      if (url) window.open(url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      showApiError(err)
    } finally {
      setExporting(false)
    }
  }, [filters, invitees.length])

  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editInvite, setEditInvite] = useState<Invitee | null>(null)
  const [editParentInvite, setEditParentInvite] = useState<Invitee | null>(null)
  const [editType, setEditType] = useState<'edit' | 'add'>('edit')
  const [actionsDialogOpen, setActionsDialogOpen] = useState(false)
  const [selectedInviteCode, setSelectedInviteCode] = useState<string | null>(
    null,
  )

  const handleResendClick = (inviteCode: string) => {
    setSelectedInviteCode(inviteCode)
    setActionsDialogOpen(true)
  }

  const handleResend = async () => {
    if (!selectedInviteCode) return
    try {
      await resendInvitation(selectedInviteCode)
      showSuccess('Invitation Send Successfully!')
      setActionsDialogOpen(false)
      setSelectedInviteCode(null)
    } catch (err) {
      showApiError(err)
    }
  }

  const handleApply = async () => {
    if (!selectedInviteCode) return
    window.open(`/apply/${selectedInviteCode}`, '_blank')
    try {
      await logoutApi()
      dispatch(logout())
    } catch {
      /* continue after apply tab opened */
    }
    setActionsDialogOpen(false)
    setSelectedInviteCode(null)
  }

  const handleEdit = async (inviteCode: string, parentInviteCode?: string) => {
    try {
      if (parentInviteCode) {
        const response = await getInviteeList({
          q: parentInviteCode,
          perPage: 1,
        })
        const parent = response.data.find(
          (inv) => inv.invite_code === parentInviteCode,
        )
        const child = parent?.co_applicant?.find(
          (c) => (c as { invite_code?: string }).invite_code === inviteCode,
        )
        if (parent && child) {
          setEditInvite(child as unknown as Invitee)
          setEditParentInvite(parent)
          setEditType('edit')
          setEditDialogOpen(true)
          return
        }
      }
      const response = await getInviteeList({ q: inviteCode, perPage: 1 })
      const invite = response.data.find((inv) => inv.invite_code === inviteCode)
      if (invite) {
        setEditInvite(invite)
        setEditParentInvite(null)
        setEditType('edit')
        setEditDialogOpen(true)
      }
    } catch (err) {
      showApiError(err)
    }
  }

  const handleAddCoApplicant = (invite: Invitee) => {
    setEditInvite(null)
    setEditParentInvite(invite)
    setEditType('add')
    setEditDialogOpen(true)
  }

  const handleEditSuccess = () => {
    setEditDialogOpen(false)
    setEditInvite(null)
    setEditParentInvite(null)
    refetch()
  }

  return (
    <div data-testid="active-invites-status-page">
      <PageShell
        title="Active Invites Status"
        pageHeaderTestIdRoot="active-invites-status-page-header"
        contentClassName="!space-y-6 w-full max-w-none min-w-0"
      >
        <ListSearchToolbar
          searchValue={searchDraft}
          searchPlaceholder="Search.."
          onSearchChange={setSearchDraft}
          onOpenFilters={() => setIsFilterOpen(true)}
          activeFilterCount={activeFilterCount}
          testIdPrefix="active-invites-status"
          actions={
            canExport && invitees.length > 0 ? (
              <button
                type="button"
                disabled={exporting || loading}
                onClick={() => void handleExport()}
                className={REPORT_EXPORT_BUTTON_CLASS}
              >
                <Download className="h-[18px] w-[18px]" aria-hidden />
                {exporting ? 'Exporting…' : 'Export'}
              </button>
            ) : undefined
          }
        />

        <InviteeFilters
          filters={filters}
          onFiltersChange={(f, meta) => applyFilters(f, meta)}
          onReset={resetFilters}
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
        />

        <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm">
          <ListPaginationBar
            placement="header"
            currentPage={pagination.current}
            lastPage={pagination.last}
            totalRecords={pagination.total}
            perPage={filters.perPage ?? 15}
            onPageChange={changePage}
            onPerPageChange={changePerPage}
            perPageOptions={[...PER_PAGE_OPTIONS]}
            testIdPrefix="active-invites-status"
            summaryLayout="angular"
          />

          {loading ? (
            <div className="flex min-h-[12rem] items-center justify-center py-16">
              <Loader2
                className="h-8 w-8 animate-spin text-[rgb(21_151_255)]"
                aria-hidden
              />
            </div>
          ) : (
            <ReportTableScroll
              enabled={invitees.length > 0}
              testIdPrefix="active-invites-status"
            >
              {isListView ? (
                <InviteeTable
                  invitees={invitees}
                  onSort={sort}
                  sortBy={filters.sortBy}
                  orderBy={filters.orderBy}
                  onResend={canEditInvite ? handleResendClick : undefined}
                  onEdit={canEditInvite ? handleEdit : undefined}
                  onAddCoApplicant={
                    canEditInvite ? handleAddCoApplicant : undefined
                  }
                  showActions={canEditInvite}
                />
              ) : (
                <InviteeGrid
                  invitees={invitees}
                  onResend={canEditInvite ? handleResendClick : undefined}
                  onEdit={canEditInvite ? handleEdit : undefined}
                  onAddCoApplicant={
                    canEditInvite ? handleAddCoApplicant : undefined
                  }
                  showActions={canEditInvite}
                />
              )}
            </ReportTableScroll>
          )}

          <ListPaginationBar
            placement="footer"
            currentPage={pagination.current}
            lastPage={pagination.last}
            totalRecords={pagination.total}
            perPage={filters.perPage ?? 15}
            onPageChange={changePage}
            onPerPageChange={changePerPage}
            perPageOptions={[...PER_PAGE_OPTIONS]}
            testIdPrefix="active-invites-status"
            summaryLayout="angular"
          />
        </div>
      </PageShell>

      <EditInviteeDialog
        isOpen={editDialogOpen}
        invite={editInvite}
        parentInvite={editParentInvite}
        type={editType}
        onClose={() => {
          setEditDialogOpen(false)
          setEditInvite(null)
          setEditParentInvite(null)
        }}
        onSuccess={handleEditSuccess}
      />

      <InviteActionsDialog
        isOpen={actionsDialogOpen}
        onClose={() => {
          setActionsDialogOpen(false)
          setSelectedInviteCode(null)
        }}
        onResend={handleResend}
        onApply={handleApply}
        showApplyNow={canApplyNow}
      />
    </div>
  )
}
